import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  PHOTO_GROUPS,
  getPhotoGroupSections,
} from '../../../case-dentist/scanUploadConfig';
import {
  buildUploadedPhotosByKey,
  buildUploadedStlScans,
  softDeleteCasePhoto,
  softDeleteCaseScan,
} from '../../../lib/caseClinicalFileSlots';
import {
  fetchSignedUrlsByFileId,
  mapSignedUrlsToPhotoKeys,
  revokePreviewBlobUrls,
} from '../../../lib/caseFileSignedUrls';
import { uploadCaseClinicalPhotos, uploadCaseStlScans } from '../../../services/caseStlPhotoUploads';
import StlArchUploadCard from '../../case-dentist-assistant/StlArchUploadCard';
import PhotoSlotUploadCard from '../../case-dentist-assistant/PhotoSlotUploadCard';

/**
 * Grille figée 2 STL + 10 photos (upload immédiat, remplacement, réinitialisation).
 * @param {{ mode?: 'all'|'scans'|'photos' }} props
 */
export default function CaseClinicalMediaBoard({
  caseId,
  patientId,
  currentUserId,
  files = [],
  canModify = false,
  onFilesUpdated,
  onDownload,
  mode = 'all',
}) {
  const [uploadingArch, setUploadingArch] = useState(null);
  const [uploadingPhotoKey, setUploadingPhotoKey] = useState(null);
  const [uploadedPhotoPreviewUrls, setUploadedPhotoPreviewUrls] = useState({});

  const showScans = mode === 'all' || mode === 'scans';
  const showPhotos = mode === 'all' || mode === 'photos';

  const uploadedStlScans = useMemo(() => buildUploadedStlScans(files), [files]);
  const uploadedPhotosByKey = useMemo(() => buildUploadedPhotosByKey(files), [files]);

  const isBusy = Boolean(uploadingArch || uploadingPhotoKey);
  const slotsDisabled = !canModify || isBusy;

  useEffect(() => {
    const photos = Object.values(uploadedPhotosByKey);
    if (!photos.length) {
      setUploadedPhotoPreviewUrls({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const signedById = await fetchSignedUrlsByFileId(photos);
      if (cancelled) {
        revokePreviewBlobUrls(signedById);
        return;
      }
      setUploadedPhotoPreviewUrls((prev) => {
        revokePreviewBlobUrls(prev);
        return mapSignedUrlsToPhotoKeys(uploadedPhotosByKey, signedById);
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [uploadedPhotosByKey]);

  const refreshAfterChange = async () => {
    if (typeof onFilesUpdated === 'function') {
      await onFilesUpdated();
    }
  };

  const handleStlSelect = async (variant, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canModify || !caseId || !patientId || !currentUserId) return;

    setUploadingArch(variant);
    try {
      const existing = variant === 'upper' ? uploadedStlScans.upper : uploadedStlScans.lower;
      if (existing?.id) await softDeleteCaseScan(existing.id);

      await uploadCaseStlScans({
        caseId,
        patientId,
        currentUserId,
        ...(variant === 'upper' ? { upperFile: file } : { lowerFile: file }),
      });
      await refreshAfterChange();
      toast.success(
        variant === 'upper' ? 'Scan supérieur enregistré' : 'Scan inférieur enregistré',
      );
    } catch (e) {
      toast.error(e.message || 'Échec du téléversement');
    } finally {
      setUploadingArch(null);
    }
  };

  const handleStlRemove = async (variant) => {
    if (!canModify) return;
    const record = variant === 'upper' ? uploadedStlScans.upper : uploadedStlScans.lower;
    if (!record?.id) return;
    setUploadingArch(variant);
    try {
      await softDeleteCaseScan(record.id);
      await refreshAfterChange();
      toast.success('Scan supprimé');
    } catch (e) {
      toast.error(e.message || 'Impossible de supprimer le scan');
    } finally {
      setUploadingArch(null);
    }
  };

  const handlePhotoSelect = async (key, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canModify || !caseId || !patientId || !currentUserId) return;

    setUploadingPhotoKey(key);
    try {
      const existing = uploadedPhotosByKey[key];
      if (existing?.id) await softDeleteCasePhoto(existing.id);

      await uploadCaseClinicalPhotos({
        photoFiles: { [key]: file },
        caseId,
        patientId,
        currentUserId,
      });
      await refreshAfterChange();
      toast.success('Photo enregistrée');
    } catch (e) {
      toast.error(e.message || 'Échec du téléversement');
    } finally {
      setUploadingPhotoKey(null);
    }
  };

  const handlePhotoRemove = async (key) => {
    if (!canModify) return;
    const record = uploadedPhotosByKey[key];
    if (!record?.id) return;
    setUploadingPhotoKey(key);
    try {
      await softDeleteCasePhoto(record.id);
      setUploadedPhotoPreviewUrls((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await refreshAfterChange();
      toast.success('Photo supprimée');
    } catch (e) {
      toast.error(e.message || 'Impossible de supprimer la photo');
    } finally {
      setUploadingPhotoKey(null);
    }
  };

  return (
    <div className="space-y-8 min-w-0">
      {!canModify && (
        <p className="text-sm text-muted-foreground rounded-lg border border-border bg-muted/30 px-3 py-2">
          Les emplacements scans et photos ne sont plus modifiables pour ce dossier.
        </p>
      )}

      {showScans && (
        <section className="space-y-4 min-w-0">
          <h3 className="text-sm font-semibold text-foreground">Scans STL</h3>
          <p className="text-xs text-muted-foreground">
            Deux emplacements fixes (arcade supérieure et inférieure). Sélectionnez un fichier pour
            téléverser immédiatement.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StlArchUploadCard
              variant="upper"
              inputId="case-detail-stl-upper"
              uploadedRecord={uploadedStlScans.upper}
              isUploading={uploadingArch === 'upper'}
              disabled={slotsDisabled}
              onFileChange={(e) => handleStlSelect('upper', e)}
              onRemove={canModify ? () => handleStlRemove('upper') : undefined}
              onDownload={
                uploadedStlScans.upper?.id && onDownload
                  ? () => onDownload(uploadedStlScans.upper.id)
                  : undefined
              }
            />
            <StlArchUploadCard
              variant="lower"
              inputId="case-detail-stl-lower"
              uploadedRecord={uploadedStlScans.lower}
              isUploading={uploadingArch === 'lower'}
              disabled={slotsDisabled}
              onFileChange={(e) => handleStlSelect('lower', e)}
              onRemove={canModify ? () => handleStlRemove('lower') : undefined}
              onDownload={
                uploadedStlScans.lower?.id && onDownload
                  ? () => onDownload(uploadedStlScans.lower.id)
                  : undefined
              }
            />
          </div>
        </section>
      )}

      {showPhotos && (
        <section className="space-y-6 min-w-0">
          <h3 className="text-sm font-semibold text-foreground">Photos cliniques</h3>
          <p className="text-xs text-muted-foreground">
            Dix emplacements obligatoires avec guide illustratif. Chaque photo est enregistrée dès la
            sélection.
          </p>
          <div className="space-y-10 min-w-0">
            {PHOTO_GROUPS.map((group) => (
              <div key={group.id} className="space-y-4 min-w-0">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${group.color}`} />
                  <h4 className="font-semibold text-foreground text-sm">{group.title}</h4>
                </div>
                {getPhotoGroupSections(group).map((section, sIdx) => (
                  <div key={`${group.id}-sec-${sIdx}`} className="space-y-3 min-w-0">
                    {section.label && section.title && (
                      <p className="text-xs font-semibold text-muted-foreground">
                        {section.label} — {section.title}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                      {section.items.map((item) => (
                        <PhotoSlotUploadCard
                          key={item.key}
                          item={item}
                          inputId={`case-detail-photo-${item.key}`}
                          uploadedRecord={uploadedPhotosByKey[item.key] || null}
                          uploadedPreviewUrl={uploadedPhotoPreviewUrls[item.key] || null}
                          isUploading={uploadingPhotoKey === item.key}
                          disabled={slotsDisabled}
                          onFileChange={(e) => handlePhotoSelect(item.key, e)}
                          onRemove={
                            canModify ? () => handlePhotoRemove(item.key) : undefined
                          }
                          onDownload={
                            uploadedPhotosByKey[item.key]?.id && onDownload
                              ? () => onDownload(uploadedPhotosByKey[item.key].id)
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
